<template>
  <div class="document-composition__configuration-wrapper">
    <div class="document-composition__scrollable document-composition__configuration">
      <div class="document-composition__configuration-header">
        <document-composition-progress />
      </div>

      <div class="document-composition__configuration-elements">
        <template v-for="configurationObject in visibleConfigurationObjects">
          <configuration-element
            class="document-composition__configuration-element"
            :key="`${configurationObject.type}-${configurationObject.id}`"
            :configuration-object="configurationObject"
            @input="onConfigurationObjectChange(configurationObject.type, configurationObject.id, $event)"
            @variableFocus="$emit('variableFocus', configurationObject.id)"
            @conditionOutcomeChange="$emit('conditionOutcomeChange', $event)"
            @duplicate="duplicateTextSegment($event)"
            @remove="removeDuplicateTextSegment($event)"
          />
          <div
            class="button"
            :class="{ 'button--nested': configurationObject.isNested }"
            :key="`${configurationObject.type}-${configurationObject.id}-duplicate`"
            v-if="configurationObject.isDuplicable" @click="duplicateTextSegment(configurationObject.textSegmentId)"
          >
            <v-icon>add</v-icon>
            <v-tooltip>Dodaj še eno polje istega tipa</v-tooltip>
            Dodaj
          </div>
        </template>

        <attachments :document="document" @change="handleAttachmentFormUpdate" />
      </div>
      <div class="document-composition__button" :class="{ 'document-composition__button--full-width': fullWidth }">
        <v-tooltip v-if="saveOrCheckoutButtonTooltip" :md-active.sync="tooltipActive" md-direction="top">{{ saveOrCheckoutButtonTooltip }}</v-tooltip>
        <v-button :disabled="!isConfigurationValid" @click="saveDocument">
          Shrani
        </v-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import prompt from 'design-system/components/prompt/prompt'
import Attachments from './Attachments.vue'
import Progress from './Progress'
import ConfigurationElement from './ConfigurationElement'
import VButton from 'design-system/VButton.vue'
import VTooltip from 'design-system/VTooltip.vue'
import VIcon from 'design-system/VIcon.vue'

export default {
  components: {
    Attachments,
    DocumentCompositionProgress: Progress,
    ConfigurationElement,
    VButton,
    VTooltip,
    VIcon,
  },
  data () {
    return {
      tooltipActive: false,
    }
  },
  props: {
    fullWidth: { type: Boolean, default: false },
  },
  computed: {
    ...mapState('pages/documentComposition', ['isEditingExistingFinalizedDocument', 'chosenConditionOutcomes']),
    ...mapGetters(['isLoggedIn']),
    ...mapGetters('pages/documentComposition', ['document', 'isConfigurationValid', 'visibleConfigurationObjects', 'isDocumentCompositionFinished', 'isEditingExistingSignedFinalizedDocument']),
    saveOrCheckoutButtonTooltip () {
      if (!this.isConfigurationValid) {
        return 'Za prenos dokumenta morate izpolniti vsa polja.'
      }
      if (!this.isDocumentCompositionFinished) {
        return this.isEditingExistingFinalizedDocument ? 'V dokumentu niste izpolnili vseh variabel, dokument lahko še vedno shranite.' : 'V dokumentu niste izpolnili vseh variabel, dokument lahko še vedno prenesete.'
      }

      return null
    },
    mappedConfigurationObjects () {
      const mappedConfigurationObjects = []
      // only display variable title for first variable in group
      let lastTitle = null
      this.visibleConfigurationObjects.forEach(configurationObject => {
        if (configurationObject.type === 'variable' && configurationObject.title === lastTitle) {
          configurationObject.title = null
        }

        mappedConfigurationObjects.push(configurationObject)
        if (configurationObject.type === 'variable') {
          lastTitle = configurationObject.title
        }
      })
      return mappedConfigurationObjects
    },
  },
  methods: {
    ...mapActions(['openAuthModal', 'setAuthModalTab']),
    ...mapActions('pages/documentComposition', ['setConfigurationObject', 'setUserAttachments', 'duplicateTextSegment', 'removeDuplicateTextSegment']),
    onConfigurationObjectChange (configurationObjectType, id, $event) {
      this.setConfigurationObject({
        configurationObjectType,
        id,
        value: $event,
      })
    },
    handleAttachmentFormUpdate (form) {
      this.setUserAttachments(form.attachments)
    },
    async saveDocument () {
      if (!this.isConfigurationValid) {
        this.tooltipActive = true
        return
      }

      let result = true
      if (this.isEditingExistingSignedFinalizedDocument) {
        result = await prompt.okText('Prekliči podpisovanje').confirm('Dokument je v procesu e-podpisovanja. Če vsebino dokumenta spremenite, bo e-podpisovanje dokumenta preklicano. Želite nadaljevati?')
      }

      if (result) {
        this.$emit('saveDocument')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'app/assets/stylesheets/variables';
@import './scrollable';

$document_composition_button_height: 80px;

.document-composition__configuration.document-composition__configuration {
  max-height: calc(#{$scrollable_max_height} - #{$document_composition_button_height});

  @include respond-to('small') {
    max-height: calc(#{$scrollable_max_height} - #{$document_composition_button_height} + 20px);
  }
}

.document-composition__configuration-header {
  position: sticky;
  z-index: 10;
  top: 0;
  overflow: hidden;
  padding-top: 16px;
  background-color: $background_color;
}

.document-composition__configuration-elements {
  padding-bottom: 92px;

  @include respond-to('small') {
    padding-bottom: 72px;
  }
}

.document-composition__configuration-header,
.document-composition__configuration-elements {
  padding-right: 24px;
}

.document-composition__configuration-element:first-of-type {
  .document-composition__input-field {
    margin-top: 0;
  }
}

.document-composition__button {
  position: absolute;
  bottom: 0;
  min-width: 258px;
  width: calc(35% - 40px);
  padding: 8px 0 20px 0;
  background-color: $light-gray;

  &--full-width {
    width: calc(100% - 32px);
    min-width: calc(100% - 32px);
  }

  @include respond-to('small') {
    padding: 16px 0;
  }
}

.document-composition__button ::v-deep  .md-button {
  width: 100%;
}

.button {
  width: fit-content;
  margin-top: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  color: white;
  background-color: $secondary_color;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  font-weight: 600;

  &--nested {
    position: relative;
    top: -12px;
    margin-left: 16px;
    margin-bottom: 8px;
  }

  .md-icon.md-icon {
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
    font-size: 16px !important;
    color: white;
    margin: 0 4px 2px 0;
  }
}
</style>
